import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spin } from "antd";
import { ReactComponent as CloseSVG } from '../../../img/default-svg/close-black.svg';
import noCheckoutImgSVG from '../../../img/default-svg/no-checkout-image.svg';
import './style.scss';
import { truncate } from "../../../utils/truncateFunction";
import TokenIcon from "../../TokenIcon";
import formatCurrency from "../../../utils/currencyFormatter";
import Button from "../../Button";
import { identity } from "../../../utils/getIdentity";
import DeactivatePlan from "./DeactivatePlan";
import { useTranslation } from "react-i18next";
import { cryptoApi } from "../../../service/cryptopay-api";
import { notificationsErrorShow } from "../../../redux/actions/notifications";
import debounce from 'lodash/debounce';

const PlanDetails = ({
  visible,
  close,
  currentValues,
  supportedCurrencies,
  supportedWallets,
  createPlan,
  editCheckoutState,
  changePlan,
  flow = 'edit',
  publishPlanChanges,
  isDuplicating,
  feesCurrency,
  fees,
  feesUsd,
  isPublishedModal,
  resetPublish,
  merchantId
}) => {
  const [currency, setCurrency] = useState('');
  const [currencyIcon, setCurrencyIcon] = useState('');
  const [owner, setOwner] = useState('');
  const [ownerDisplay, setOwnerDisplay] = useState({ currencyName: '', balanceValue: '', balanceUsd: 0 });
  const [isOpenDeactivate, setIsOpenDeactivate] = useState(false);
  const { t } = useTranslation();
  const [feesPublish, setFeesPublish] = useState('');
  const [feesUsdPublish, setFeesUsdPublish] = useState('');
  const [feesCurrencyPublish, setFeesCurrencyPublish] = useState('');

  const debouncedGetFeeEstimatePlan = useRef(
    debounce(async (data, id) => {
      try {
        const response = await cryptoApi.planEstimateMax(id, data);
        if (response?.data) {
          setFeesPublish(response.data?.fee);
          setFeesUsdPublish(response.data?.priceUsd);
          setFeesCurrencyPublish(response.data?.currencyFee);
        }
      } catch (error) {
        setFeesPublish('');
        setFeesUsdPublish('');
        setFeesCurrencyPublish('');
        notificationsErrorShow({ message: error.data.message });
      }
    }, 1000)
  );

  useEffect(() => {
    if (
      currentValues?.name &&
      currentValues?.owner &&
      currentValues?.payeeAddress &&
      currentValues?.periodType &&
      currentValues?.price &&
      currentValues?.currency &&
      isPublishedModal &&
      merchantId
    ) {
      debouncedGetFeeEstimatePlan.current({
        name: currentValues?.name,
        owner: currentValues?.owner,
        payeeAddress: currentValues?.payeeAddress,
        periodType: currentValues?.periodType,
        price: currentValues?.price,
        currency: currentValues?.currency,
      }, merchantId);
    }
  }, [
    currentValues?.name,
    currentValues?.owner,
    currentValues?.payeeAddress,
    currentValues?.periodType,
    currentValues?.price,
    currentValues?.currency,
    merchantId
  ]);

  useEffect(() => {
    if (supportedCurrencies?.data && currentValues) {
      const matchedCurrency = supportedCurrencies.data.find((item) => item?._id === currentValues?.currency);

      if (matchedCurrency) {
        setCurrency(matchedCurrency?.title);
        setCurrencyIcon(matchedCurrency?.name);
      }
    }

    if (supportedWallets?.data && currentValues) {
      const matchedOwner = supportedWallets.data.find((item) => item?.address === currentValues?.owner)
        || supportedWallets.data.find((item) => item?._id === currentValues?.owner);

      if (matchedOwner) {
        setOwner(matchedOwner);

        const walletVer = editCheckoutState?.owner?.walletVersion || editCheckoutState?.walletVersion || localStorage.getItem('walletVersion');
        if (walletVer === 'v1') {
          const v1CurrencyName =
            matchedOwner?.currency ||
            supportedCurrencies?.data?.find(c => c?.mainCurrency === matchedOwner?.currencyId)?.name ||
            '';
          setOwnerDisplay({
            currencyName: v1CurrencyName,
            balanceValue: matchedOwner?.balance?.value ?? 0,
            balanceUsd: matchedOwner?.balance?.usd ?? 0,
          });
        } else {
          const selectedCurrency = supportedCurrencies?.data?.find(c => c?._id === currentValues?.currency);
          const targetCurrencyId = selectedCurrency?.mainCurrency;
          const matchedBalance = matchedOwner?.balances?.find(b => b?.currency?.id === targetCurrencyId)
            || matchedOwner?.balances?.[0];

          setOwnerDisplay({
            currencyName: matchedBalance?.currency?.name || '',
            balanceValue: matchedBalance?.balance?.value ?? 0,
            balanceUsd: matchedBalance?.balance?.usd ?? 0,
          });
        }
      }
    }
  }, [currentValues, supportedCurrencies, supportedWallets]);

  const updatePlan = (status) => {
    if (!status) {
      changePlan(!status, editCheckoutState?.published)
    } else {
      setIsOpenDeactivate(true)
    }
  }

  const handleClose = () => {
    close();
    if (isPublishedModal) {
      resetPublish();
    }
  }
  return (
    <Modal
      width={620}
      title=""
      visible={visible}
      onCancel={handleClose}
      footer={null}
      className={`checkout-modal checkout-modal-${identity}`}
      centered
      closeIcon={<CloseSVG />}
      destroyOnClose
      maskClosable={false}
    >
      <div className={`checkout-modal-content checkout-modal-content-${identity}`}>
        {editCheckoutState && flow === 'edit' ? (
          <div className={`checkout-modal-content__titlePlan checkout-modal-content__titlePlan-${identity}`}>
            {editCheckoutState?.status ? (
              <>{t('admin.update_or_deactivate')}</>
            ) : (
              <>{t('admin.update_or_activate')}</>
            )}
          </div>
        ) : (
          <div className={`checkout-modal-content__titlePlan checkout-modal-content__titlePlan-${identity}`}>
            {t('admin.publish_plan')}
          </div>
        )}

        {editCheckoutState && flow === 'edit' ? (
          <div className={`checkout-modal-content__subtitle checkout-modal-content__subtitle-${identity}`}>
          </div>
        ) : (
          <div className={`checkout-modal-content__subtitle checkout-modal-content__subtitle-${identity}`}>
            {t('admin.publish_stop')}
          </div>
        )}

        <div className="checkout-modal-content__body">
          <div className={`checkout-modal-content__body--line checkout-modal-content__body--line-${identity}`}>
            <div className="checkout-modal-content__body--title">{t('admin.plan_name')}:</div>
            <div className="checkout-modal-content__body--value">{currentValues?.name || "-"}</div>
          </div>
          <div className={`checkout-modal-content__body--line checkout-modal-content__body--line-${identity}`}>
            <div className="checkout-modal-content__body--title">{t('admin.plan_description')}:</div>
            <div className="checkout-modal-content__body--value">{currentValues?.description || "-"}</div>
          </div>
          <div className={`checkout-modal-content__body--line checkout-modal-content__body--line-${identity}`}>
            <div className="checkout-modal-content__body--title">{t('admin.payee_address')}:</div>
            <div
              className="checkout-modal-content__body--value">{truncate(currentValues?.payeeAddress, 14, "..") || "-"}</div>
          </div>
          <div className={`checkout-modal-content__body--line checkout-modal-content__body--line-${identity}`}>
            <div className="checkout-modal-content__body--title">{t('admin.price')}:</div>
            <div className="checkout-modal-content__body--value">
              {currentValues?.price || "-"}
              {" "}
              {currencyIcon && <TokenIcon tokenName={currencyIcon} />}
              {" "}
              {currency || "-"}
            </div>
          </div>
          <div className={`checkout-modal-content__body--line checkout-modal-content__body--line-${identity}`}>
            <div className="checkout-modal-content__body--title">{t('admin.owner')}:</div>
            <div className="checkout-modal-content__body--value">
              <div className="checkout-modal-content__body__owner">
                <div className="checkout-modal-content__body__owner--address">
                  {ownerDisplay?.currencyName && <TokenIcon tokenName={ownerDisplay.currencyName} />}
                  {truncate(owner?.address, 14, "..")}
                </div>
                <div className="checkout-modal-content__body__owner--balance">
                  <div className="checkout-modal-content__body__owner--balance-crypto">
                    {ownerDisplay?.balanceValue?.toString()?.length > 11
                      ? `${ownerDisplay.balanceValue.toString().slice(0, 12)}`
                      : ownerDisplay?.balanceValue}
                    {" "}
                    {ownerDisplay?.currencyName}
                  </div>
                  <div
                    className="checkout-modal-content__body__owner--balance-usd">{formatCurrency(ownerDisplay?.balanceUsd)}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="checkout-modal-content__body--br"></div>
          <div className={`checkout-modal-content__body--line checkout-modal-content__body--line-${identity}`}>
            <div className="checkout-modal-content__body--title">{t('admin.plan_photo')}:</div>
            <div className="checkout-modal-content__body--image">
              <img
                src={currentValues?.image && typeof currentValues?.image === "string" ? `${process.env.REACT_APP_API_URL}${currentValues?.image}` : currentValues?.image && typeof currentValues?.image !== "string" ? URL.createObjectURL(currentValues?.image) : noCheckoutImgSVG}
                alt="" />
            </div>
          </div>
          <div className={`checkout-modal-content__body--line checkout-modal-content__body--line-${identity}`}>
            <div className="checkout-modal-content__body--title">{t('admin.plan_logo')}:</div>
            <div className="checkout-modal-content__body--image">
              <img
                src={currentValues?.logoImage && typeof currentValues?.logoImage === "string" ? `${process.env.REACT_APP_API_URL}${currentValues?.logoImage}` : currentValues?.logoImage && typeof currentValues?.logoImage !== "string" ? URL.createObjectURL(currentValues?.logoImage) : noCheckoutImgSVG}
                alt="" />
            </div>
          </div>
          <div className={`checkout-modal-content__body--line checkout-modal-content__body--line-${identity}`}>
            <div className="checkout-modal-content__body--title">{t('admin.background_color')}:</div>
            <div style={{
              backgroundColor: currentValues?.backgroundColor,
              width: "50px",
              height: "24px",
              borderRadius: "6px"
            }}></div>
          </div>
          <div className={`checkout-modal-content__body--line checkout-modal-content__body--line-${identity}`}>
            <div className="checkout-modal-content__body--title">{t('admin.accent_color')}:</div>
            <div style={{
              backgroundColor: currentValues?.accentColor,
              width: "50px",
              height: "24px",
              borderRadius: "6px"
            }}></div>
          </div>

          {!editCheckoutState && flow === "edit" && (
            <div className="checkout-modal-content__body--button">
              <Button onClick={() => createPlan(false)} type="secondary">{t('admin.save_draft')}</Button>
              <Button onClick={() => createPlan(true)} type="primary">{t('admin.publish')}</Button>
            </div>
          )}

          {editCheckoutState && flow === "publish" && isDuplicating && (
            <div className="checkout-modal-content__body--button">
              <Button onClick={() => createPlan(false)} type="secondary">{t('admin.save_draft')}</Button>
              <Button onClick={() => createPlan(true)} type="primary">{t('admin.publish')}</Button>
            </div>
          )}

          {editCheckoutState && flow === "publish" && !isDuplicating && (
            <div className="checkout-modal-content__body--button">
              <Button onClick={close} type="secondary">{t('admin.save_draft')}</Button>
              <Button onClick={() => publishPlanChanges(editCheckoutState?._id)} type="primary">{t('admin.publish')}</Button>
            </div>
          )}

          {editCheckoutState && flow === "edit" && (
            <div className="checkout-modal-content__body--button">
              <Button
                onClick={() => updatePlan(editCheckoutState?.status)}
                className={`${editCheckoutState?.status ? "checkout-modal-content__body--button-deactivate" : "checkout-modal-content__body--button-activate"}`}
              >
                {editCheckoutState?.status ? t('admin.deactivate_plans') : t('admin.activate_plan')}
              </Button>
              <Button onClick={() => changePlan(editCheckoutState?.status, editCheckoutState?.published)} type="primary">{t('admin.update')}</Button>
            </div>
          )}

          {(!editCheckoutState || isDuplicating) && fees && feesCurrency && (
            <div className={`checkout-set-data__flex--estimate checkout-set-data__flex--estimate-${identity}`}>
              <div>{t('admin.publication_estimate')}:</div>
              <div>
                {fees} {feesCurrency} ({formatCurrency(+feesUsd)})
              </div>
            </div>
          )}

          {isPublishedModal && feesPublish && feesCurrencyPublish && (
            <div className={`checkout-set-data__flex--estimate checkout-set-data__flex--estimate-${identity}`}>
              <div>{t('admin.publication_estimate')}:</div>
              <div>
                {feesPublish} {feesCurrencyPublish} ({formatCurrency(+feesUsdPublish)})
              </div>
            </div>
          )}

          <div className={`checkout-set-data__non-revertible checkout-set-data__non-revertible-${identity}`}>{t("checkouts.plan.0.nonRevertible")}</div>
        </div>
      </div>

      <DeactivatePlan changePlan={changePlan} currentValues={editCheckoutState} visible={isOpenDeactivate}
                      close={() => setIsOpenDeactivate(false)} />
    </Modal>
  )
};

export default PlanDetails;